import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter/dist/esm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getPost, getPosts } from '../../shared/api/posts';
import { Post } from '../../shared/types/post';
import { Mermaid } from '../../shared/components/Mermaid';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getI18nStrings } from '../../shared/data/i18n';
import { getUserHomePath, getUserPostsPath } from '../../shared/config/env';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { setStructuredData, createArticleStructuredData, createBreadcrumbStructuredData, removeStructuredData } from '../../shared/utils/structuredData';
import { getSiteUrl } from '../../shared/utils/metaTags';
import { isOwnDomainUrl, extractSlugFromUrl, extractLangFromUrl } from '../../shared/utils/urlUtils';
import ArticleLinkCard from '../components/ArticleLinkCard';
import LanguageNoticeBanner from '../../shared/components/LanguageNoticeBanner';
import '../../shared/styles/markdown-content.css';
import './PostDetail.css';

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

/**
 * 自ドメインURLの記事リンクカードを表示するラッパーコンポーネント
 * 非同期で記事を取得し、カードを表示する
 */
function ArticleLinkCardWrapper({ href, slug, lang }: { href: string; slug: string; lang: 'ja' | 'en' }) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await getPost(slug, lang);
        // 公開済み記事のみカードを表示
        if (data.status === 'published') {
          setPost(data);
        } else {
          setError(true);
        }
      } catch (err) {
        // エラー時は通常のリンクにフォールバック
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, lang]);

  // ローディング中またはエラー時は通常のリンクを表示
  if (loading || error || !post) {
    const isOwnDomain = isOwnDomainUrl(href);
    return (
      <a 
        href={href} 
        {...(isOwnDomain ? {} : { target: "_blank", rel: "noopener noreferrer" })}
      >
        {href}
      </a>
    );
  }

  // 記事が取得できた場合はカードを表示
  return <ArticleLinkCard post={post} lang={lang} url={href} />;
}

/**
 * PostIDベースの記事リンクカードを表示するラッパーコンポーネント
 * 非同期で記事を取得し、カードを表示する
 */
function ArticleLinkCardWrapperByPostId({ postId, lang }: { postId: string; lang: 'ja' | 'en' }) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const postsPath = getUserPostsPath();
  const siteUrl = getSiteUrl();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(false);
        // PostIDで記事を取得（公開済み記事のみ）
        const response = await getPosts({ 
          post_ids: [postId], 
          lang,
          status: 'published',
          skipAuth: true 
        });
        
        if (response.posts && response.posts.length > 0) {
          const data = response.posts[0];
          // 公開済み記事のみカードを表示
          if (data.status === 'published') {
            setPost(data);
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        // エラー時は通常のリンクにフォールバック
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, lang]);

  // ローディング中またはエラー時は何も表示しない（空のリンクテキストなので）
  if (loading || error || !post) {
    return null;
  }

  // 記事が取得できた場合はカードを表示
  // 現在の言語に合わせたURLを生成
  const url = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}${postsPath}/${post.slug}`;
  return <ArticleLinkCard post={post} lang={lang} url={url} />;
}

function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const lang = useLanguage();
  const i18n = getI18nStrings(lang);
  const navigate = useNavigate();
  const homePath = getUserHomePath();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const [headerHeight, setHeaderHeight] = useState<number>(80); // デフォルト値
  const [footerHeight, setFooterHeight] = useState<number>(0); // フッターの高さ（paddingBottomを含まない元の高さ）
  const [tocHeight, setTocHeight] = useState<number>(0); // TOCの高さ
  const [isTocExpanded, setIsTocExpanded] = useState<boolean>(false);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  const headingsRef = useRef<HeadingItem[]>([]);
  const headingRenderIndexRef = useRef<number>(0);
  const headingUsageMapRef = useRef<Map<string, Set<number>>>(new Map()); // 見出しの使用済みインデックスを追跡
  const previousFooterHeightRef = useRef<number>(0); // 前のフッターの高さを保持
  const previousMarginBottomRef = useRef<number>(0); // 前のmarginBottomを保持
  const originalFooterHeightRef = useRef<number>(0); // paddingBottomを追加する前の元のフッターの高さを保持
  const isUpdatingFooterHeightRef = useRef<boolean>(false); // updateFooterHeightが実行中かどうかを保持

  // フッターの高さを取得
  useEffect(() => {
    if (!post) return; // postが存在する場合のみ実行
    
    const updateFooterHeight = () => {
      // 既に実行中の場合はスキップ（無限ループを防ぐ）
      if (isUpdatingFooterHeightRef.current) return;
      
      const footer = document.querySelector('.site-footer');
      const footerContainer = document.querySelector('.site-footer__container') as HTMLElement;
      if (footer && footerContainer) {
        isUpdatingFooterHeightRef.current = true;
        
        // paddingBottomを一時的に削除して、元のフッターの高さを取得
        const originalPaddingBottom = footerContainer.style.paddingBottom;
        footerContainer.style.paddingBottom = '';
        
        // レイアウトが再計算されるまで少し待つ
        requestAnimationFrame(() => {
          const height = footer.getBoundingClientRect().height;
          
          // paddingBottomを元に戻す
          footerContainer.style.paddingBottom = originalPaddingBottom;
          
          // 高さが実際に変更された場合のみ更新（無限ループを防ぐ）
          if (Math.abs(height - previousFooterHeightRef.current) > 1) {
            previousFooterHeightRef.current = height;
            originalFooterHeightRef.current = height; // 元の高さを保持
            setFooterHeight(height);
          }
          
          // 実行完了フラグをリセット
          setTimeout(() => {
            isUpdatingFooterHeightRef.current = false;
          }, 50);
        });
      }
    };

    // DOMが完全にレンダリングされた後に実行
    const timer = setTimeout(() => {
      updateFooterHeight();
    }, 100);
    
    // リサイズ時にも更新
    window.addEventListener('resize', updateFooterHeight);
    // MutationObserverでフッターの変更を監視（ただし、debounceを追加）
    // updateFooterHeightが実行中の場合や、paddingBottomの変更は無視する（無限ループを防ぐため）
    let observerTimer: NodeJS.Timeout | null = null;
    const observer = new MutationObserver((mutations) => {
      // updateFooterHeightが実行中の場合は無視
      if (isUpdatingFooterHeightRef.current) return;
      
      // paddingBottomの変更を無視
      const hasNonPaddingChange = mutations.some(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;
          const oldValue = mutation.oldValue || '';
          const newValue = target.getAttribute('style') || '';
          // paddingBottomの変更のみの場合は無視
          const oldPaddingBottom = oldValue.match(/padding-bottom[^;]*/)?.[0] || '';
          const newPaddingBottom = newValue.match(/padding-bottom[^;]*/)?.[0] || '';
          if (oldPaddingBottom !== newPaddingBottom && oldValue.replace(/padding-bottom[^;]*;?/g, '') === newValue.replace(/padding-bottom[^;]*;?/g, '')) {
            return false; // paddingBottomの変更のみの場合は無視
          }
        }
        return true;
      });
      
      if (hasNonPaddingChange) {
        if (observerTimer) clearTimeout(observerTimer);
        observerTimer = setTimeout(updateFooterHeight, 100);
      }
    });
    const footer = document.querySelector('.site-footer');
    if (footer) {
      observer.observe(footer, { 
        childList: true, 
        subtree: true, 
        attributes: true, 
        attributeOldValue: true,
        attributeFilter: ['style', 'class'] 
      });
    }

    return () => {
      clearTimeout(timer);
      if (observerTimer) clearTimeout(observerTimer);
      window.removeEventListener('resize', updateFooterHeight);
      observer.disconnect();
    };
  }, [post]); // postが読み込まれた後にも再計算

  // ヘッダーの高さを取得
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('.site-header');
      if (header) {
        const height = header.getBoundingClientRect().height;
        setHeaderHeight(height);
      }
    };

    // DOMが完全にレンダリングされた後に実行
    const timer = setTimeout(() => {
      updateHeaderHeight();
    }, 100);

    // リサイズ時にも更新
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [post]); // postが読み込まれた後にも再計算

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setError(i18n.postDetail.slugNotSpecified);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getPost(slug, lang);
        setPost(data);
        setRelatedPosts(data?.related_posts ?? []);
      } catch (err) {
        if (err instanceof Error) {
          // 404エラー（記事が見つかりません）の場合は404ページにリダイレクト
          // loadingを表示せずに即座にリダイレクト
          if (err.message === '記事が見つかりません') {
            setLoading(false);
            navigate(`/${lang}/not-found-404`, { replace: true });
            return;
          }
          setError(err.message);
        } else {
          setError(i18n.postDetail.fetchError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, lang, i18n.postDetail.slugNotSpecified, i18n.postDetail.fetchError]);

  // メタタグと構造化データの設定
  const postsPath = getUserPostsPath();
  const siteUrl = getSiteUrl();
  
  // メタタグ用のデータを準備
  const metaTagsData = useMemo(() => {
    if (!post || post.status !== 'published') {
      return null;
    }

    const postUrl = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}${postsPath}/${post.slug}`;
    const description = post.excerpt || post.content.substring(0, 160).replace(/[#*`]/g, '').trim();
    // サムネイル画像がない場合はデフォルトのOGP画像を使用（XのOGP検証ツールで必須）
    const imageUrl = post.thumbnail_url || '/og-image.png';

    return {
      title: `${post.title} | Version0.1`,
      description,
      ogp: {
        title: post.title,
        description,
        image: imageUrl,
        url: postUrl,
        type: 'article',
        siteName: 'Version0.1',
      },
      twitterCard: {
        card: 'summary_large_image' as const,
        title: post.title,
        description,
        image: imageUrl,
      },
      canonicalUrl: postUrl,
    };
  }, [post, lang, postsPath, siteUrl]);

  // メタタグを設定
  useMetaTags(metaTagsData || {
    title: 'Version0.1',
    description: lang === 'ja' ? 'Version0.1 - やりたいところだけ実装するので永遠に完成しない。日々の作業ログや考えたことを残す技術ブログ。AWS中心の開発環境で、完成しないプロジェクトの記録と技術記事を公開' : 'Version0.1 - Will never be completed because I only implement what I want. A technical blog documenting daily work logs and thoughts. Sharing technical articles and project records, primarily using AWS',
  });

  // 構造化データを設定
  useEffect(() => {
    if (!post || post.status !== 'published' || !metaTagsData) {
      return;
    }

    const postUrl = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}${postsPath}/${post.slug}`;
    const description = post.excerpt || post.content.substring(0, 160).replace(/[#*`]/g, '').trim();
    const imageUrl = post.thumbnail_url || undefined;

    // Article構造化データを設定
    const articleData = createArticleStructuredData({
      headline: post.title,
      description,
      image: imageUrl,
      datePublished: post.published_at || undefined,
      dateModified: post.updated_at,
      author: {
        name: 'ブログ管理者', // TODO: 実際の著者名に変更
      },
      publisher: {
        name: 'Version0.1',
      },
      url: postUrl,
    });
    setStructuredData('article', articleData);

    // BreadcrumbList構造化データを設定
    const breadcrumbItems = [
      { name: 'ホーム', url: `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}` },
      { name: post.title, url: postUrl },
    ];
    const breadcrumbData = createBreadcrumbStructuredData(breadcrumbItems);
    setStructuredData('breadcrumb', breadcrumbData);

    // クリーンアップ
    return () => {
      removeStructuredData('article');
      removeStructuredData('breadcrumb');
    };
  }, [post, lang, postsPath, siteUrl, metaTagsData]);

  // Twitter埋め込みスクリプトの読み込み
  useEffect(() => {
    if (!post || post.status !== 'published') return;

    // コンテンツにTwitter埋め込みが含まれているかチェック
    const hasTwitterEmbed = post.content.includes('twitter-tweet') || post.content.includes('platform.twitter.com/widgets.js');
    
    if (hasTwitterEmbed) {
      // 既にスクリプトが読み込まれているかチェック
      const existingScript = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
      
      if (!existingScript) {
        // Twitter埋め込みスクリプトを動的に読み込む
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.charset = 'utf-8';
        document.body.appendChild(script);

        // スクリプトが読み込まれたら、Twitterウィジェットを再レンダリング
        script.onload = () => {
          if (window.twttr && window.twttr.widgets) {
            window.twttr.widgets.load();
          }
        };

        return () => {
          // クリーンアップ時にスクリプトを削除
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      } else {
        // 既にスクリプトが存在する場合、ウィジェットを再レンダリング
        if (window.twttr && window.twttr.widgets) {
          window.twttr.widgets.load();
        }
      }
    }
  }, [post]);

  // カテゴリー名はAPIから既に適切な言語で返されているため、そのまま使用
  const getCategoryName = useMemo(() => {
    return post?.category || null;
  }, [post?.category]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return i18n.postDetail.dateFormat(year, month, day, hour, minute);
  };

  // サムネイルURLを検証（PostCardと同じロジック）
  const isValidThumbnailUrl = useMemo(() => {
    if (!post?.thumbnail_url) return false;
    
    let url = post.thumbnail_url.trim();
    if (!url) return false;
    
    let fullUrl: string;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      fullUrl = url;
    } else if (url.startsWith('//')) {
      fullUrl = `https:${url}`;
    } else if (url.includes('.')) {
      fullUrl = `https://${url}`;
    } else {
      return false;
    }
    
    try {
      const urlObj = new URL(fullUrl);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }
      
      // TODO: サムネイル画像保管機能実装後、このチェックを削除
      if (urlObj.hostname === 'example.com' || urlObj.hostname.endsWith('.example.com')) {
        return false;
      }
      if (urlObj.hostname === 'via.placeholder.com') {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }, [post?.thumbnail_url]);

  const handleImageError = () => {
    setImageError(true);
  };

  const displayThumbnailUrl = isValidThumbnailUrl && !imageError && post?.thumbnail_url
    ? (() => {
        let url = post.thumbnail_url!.trim();
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        } else if (url.startsWith('//')) {
          return `https:${url}`;
        } else if (url.includes('.')) {
          return `https://${url}`;
        }
        return null;
      })()
    : null;

  // 見出しを抽出してTOCを生成（見出しの種類と配置順に基づいたIDを生成）
  // ID形式: h{レベル}-{そのレベルの見出しが何個目か}
  // 例: h1-1 (最初のh1), h2-1 (最初のh2), h2-2 (2番目のh2), h4-126 (126個目のh4)
  // このIDはTOCと見出しコンポーネントで同じロジックを使用して生成されるため、常に一致する
  // コードブロック内の行は除外する（HTMLタグのみを対象）
  const toc = useMemo(() => {
    if (!post?.content) return [];
    
    const lines = post.content.split('\n');
    const headings: HeadingItem[] = [];
    const levelCounts: Record<number, number> = {}; // 各レベルの見出しの個数をカウント
    let inCodeBlock = false; // コードブロック内かどうかを追跡
    
    lines.forEach((line) => {
      // フェンス付きコードブロックの開始/終了を検出
      const codeBlockMatch = line.match(/^```/);
      if (codeBlockMatch) {
        inCodeBlock = !inCodeBlock;
        return; // コードブロックの境界行自体はスキップ
      }
      
      // コードブロック内の場合は見出しを抽出しない
      if (inCodeBlock) {
        return;
      }
      
      // インデント付きコードブロック（4スペースまたはタブで始まる行）も除外
      if (/^[ \t]{4,}/.test(line)) {
        return;
      }
      
      // 見出しを抽出（HTMLタグのみを対象）
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        // バッククォートを削除（Markdownのインラインコードを正規化）
        const text = match[2].trim().replace(/`/g, '');
        
        // 見出しの種類と配置順に基づいたIDを生成
        // 各レベルの見出しが何個目かをカウント（例: h1-1, h2-1, h2-2, h3-1）
        if (!levelCounts[level]) {
          levelCounts[level] = 0;
        }
        levelCounts[level]++;
        const id = `h${level}-${levelCounts[level]}`;
        
        headings.push({ id, text, level });
      }
    });
    
    headingsRef.current = headings;
    headingRenderIndexRef.current = 0; // レンダリングインデックスをリセット
    headingUsageMapRef.current.clear(); // 使用回数マップをクリア
    
    return headings;
  }, [post?.content]);

  // post.contentが変更されたときにheadingRenderIndexRefをリセット
  // ReactMarkdownがレンダリングされる前に確実にリセットされるようにする
  useEffect(() => {
    headingRenderIndexRef.current = 0;
    headingUsageMapRef.current.clear();
  }, [post?.content]);

  // ReactMarkdownがレンダリングされる直前にheadingRenderIndexRefをリセット
  // useLayoutEffectを使用して、DOM更新前に確実にリセットする
  useLayoutEffect(() => {
    headingRenderIndexRef.current = 0;
    headingUsageMapRef.current.clear();
  });

  // URLのハッシュから該当要素にスクロール（描画前に実行）
  // ハッシュがない場合のページトップへのスクロールは、ScrollToTopコンポーネントで処理される
  useLayoutEffect(() => {
    if (!post) return;

    const hash = window.location.hash?.slice(1) || ''; // #を除く

    if (hash && toc.length > 0) {
      // ハッシュがある場合は、DOMが完全にレンダリングされるのを待ってからスクロール
      // requestAnimationFrameを使用して、次のフレームで実行（DOMが描画される前）
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const element = document.getElementById(hash);
          if (element) {
            // handleHeadingClickと同じロジックを使用して、正確な位置にスクロール
            // scroll-margin-topを取得（CSS変数または固定値）
            const scrollMarginTopStr = getComputedStyle(element).scrollMarginTop;
            const scrollMarginTop = parseFloat(scrollMarginTopStr) || (headerHeight + 16);
            
            // getBoundingClientRect()を使用してビューポートからの位置を取得し、
            // 現在のスクロール位置を加算してページ全体からの位置を計算
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            
            // scroll-margin-topを考慮してスクロール位置を計算
            const offsetPosition = elementTop - scrollMarginTop;

            // アニメーションなしで即座にスクロール
            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'auto',
            });

            // アクティブな見出しIDを更新
            setActiveHeadingId(hash);
          }
        });
      });
    }
  }, [toc, post, headerHeight]);

  // スクロールに合わせてアクティブな見出しを更新
  useEffect(() => {
    if (toc.length === 0) return;

    const handleScroll = () => {
      const headings = headingsRef.current;
      if (headings.length === 0) return;

      // 実際のヘッダーの高さ + 余白（16px）をオフセットとして使用
      const offset = headerHeight + 16;
      const scrollPosition = window.scrollY + offset;
      let currentHeading = '';
      let maxElementTop = -Infinity; // スクロール位置を通過した見出しのうち、最も下の見出しの位置

      // すべての見出しをチェックして、スクロール位置を通過した見出しのうち、最も下の見出しを選択
      for (let i = 0; i < headings.length; i++) {
        const element = document.getElementById(headings[i].id);
        if (element) {
          // getBoundingClientRect()を使用してページ全体からの正確な位置を取得
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.pageYOffset;
          const elementHeight = rect.height;
          
          // 見出しの高さを考慮して、見出しの上部位置を基準に判定
          // 見出しの高さが大きい場合（h1など）は、見出しの上部が画面の先頭に到達した時点で選択する
          // 見出しの高さが小さい場合（h3など）は、見出しの上部が画面の先頭に到達した時点で選択する
          // 見出しの実際の高さの30%をオフセットとして使用（最大20px、最小5px）
          // これにより、見出しが画面の先頭に到達した時点で、TOCが正しく選択される
          const heightBasedOffset = Math.max(5, Math.min(elementHeight * 0.3, 20));
          const elementThreshold = elementTop + heightBasedOffset;
          
          // スクロール位置が見出しの閾値位置を通過しているかチェック
          // 見出しの上部位置（高さに応じたオフセット付き）が画面の先頭に到達した時点で選択する
          if (scrollPosition >= elementThreshold) {
            // より下の見出し（より大きなelementTop）を見つけた場合、それを選択
            if (elementTop > maxElementTop) {
              maxElementTop = elementTop;
              currentHeading = headings[i].id;
            }
          }
        }
      }

      if (currentHeading && currentHeading !== activeHeadingId) {
        setActiveHeadingId(currentHeading);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初期状態を設定

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [toc, activeHeadingId, headerHeight]);

  // TOCの高さを取得
  useEffect(() => {
    if (!post || toc.length === 0) {
      setTocHeight(0);
      return;
    }
    
    const updateTocHeight = () => {
      const tocElement = document.querySelector('.post-detail__toc');
      if (tocElement) {
        const height = tocElement.getBoundingClientRect().height;
        setTocHeight(height);
      }
    };
    
    const timer = setTimeout(() => {
      updateTocHeight();
    }, 100);
    
    window.addEventListener('resize', updateTocHeight);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTocHeight);
    };
  }, [post, toc.length]);

  // フッターのコンテナ（site-footer__container）の下にマージンを追加（フッターの最終行までスクロールできるように）
  useEffect(() => {
    if (!post) return;
    
    const updateFooterMargin = () => {
      // スマホ版（768px以下）かどうかをチェック
      const isMobile = window.innerWidth <= 768;
      
      // フッターのコンテナを取得（ユーザーの要望通り、コンテナにpaddingBottomを追加）
      const footerContainer = document.querySelector('.site-footer__container') as HTMLElement;
      if (!footerContainer) return;
      
      // フッターの高さ分のパディングを追加（フッターの最終行までスクロールできるように）
      // スマホ版のみに適用（PC版では不要）
      let calculatedPadding = 0;
      
      // スマホ版のみにパディングを追加
      if (isMobile) {
        // スマホ版では、TOCの高さのみを追加
        // フッター自体は既に存在しているため、フッターの高さを追加する必要はない
        // TOCが画面下部に固定されているため、フッターの最終行までスクロールするにはTOCの高さ分のスペースが必要
        if (toc.length > 0) {
          // TOCの現在の高さを使用（折りたたみ時は約44px、展開時は60vhまで）
          // 必要最小限のスペースを確保するため、現在の高さを使用
          const tocMinHeight = tocHeight > 0 ? tocHeight : 44; // 折りたたみ時の最小高さ（44px）
          calculatedPadding = tocMinHeight;
        }
      }
      // PC版ではパディングを追加しない（calculatedPadding = 0のまま）
      
      // パディングが実際に変更された場合のみ更新（無限ループを防ぐ）
      const paddingDiff = Math.abs(calculatedPadding - previousMarginBottomRef.current);
      if (paddingDiff > 1) {
        previousMarginBottomRef.current = calculatedPadding;
        footerContainer.style.paddingBottom = calculatedPadding > 0 ? `${calculatedPadding}px` : '';
      } else if (calculatedPadding === 0 && previousMarginBottomRef.current > 0) {
        // calculatedPaddingが0で、以前に設定されたpaddingBottomがある場合はクリア
        previousMarginBottomRef.current = 0;
        footerContainer.style.paddingBottom = '';
      }
    };
    
    // フッターの高さが取得されたら更新
    const timer = setTimeout(() => {
      updateFooterMargin();
    }, 100);
    
    // リサイズ時にも再計算（スマホ版とPC版の切り替えに対応）
    window.addEventListener('resize', updateFooterMargin);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateFooterMargin);
      
      // コンポーネントがアンマウントされる際に、paddingBottomをリセット
      // これにより、Home画面など他のページでpaddingBottomが残らないようにする
      const footerContainer = document.querySelector('.site-footer__container') as HTMLElement;
      if (footerContainer) {
        footerContainer.style.paddingBottom = '';
        previousMarginBottomRef.current = 0;
      }
    };
  }, [post, footerHeight, tocHeight, toc.length]);

  // 見出しクリック時のスムーズスクロール
  const handleHeadingClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      // scroll-margin-topを取得（CSS変数または固定値）
      const scrollMarginTopStr = getComputedStyle(element).scrollMarginTop;
      const scrollMarginTop = parseFloat(scrollMarginTopStr) || (headerHeight + 16);
      
      // getBoundingClientRect()を使用してビューポートからの位置を取得し、
      // 現在のスクロール位置を加算してページ全体からの位置を計算
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + window.pageYOffset;
      
      // scroll-margin-topを考慮してスクロール位置を計算
      // 見出しが確実に画面の先頭に来るように、scrollMarginTopをそのまま使用
      // （追加の調整は不要。scrollMarginTopが既に適切な値になっている）
      const offsetPosition = elementTop - scrollMarginTop;

      window.scrollTo({
        top: Math.max(0, offsetPosition), // 負の値にならないようにする
        behavior: 'smooth',
      });
      
      // スクロール完了後の位置を確認（スムーズスクロールの完了を待つ）
      setTimeout(() => {
        const finalRect = element.getBoundingClientRect();
        const difference = finalRect.top - scrollMarginTop;
        
        // もし期待位置より下に来ている場合は、微調整を行う
        if (difference > 1) {
          const adjustment = difference;
          window.scrollTo({
            top: window.pageYOffset - adjustment,
            behavior: 'smooth',
          });
        }
      }, 1000); // スムーズスクロールの完了を待つ（通常500-800ms）
      
      // URLのハッシュを更新
      window.history.pushState(null, '', `#${id}`);
      
      // アクティブな見出しIDを即座に更新（TOCクリックを優先）
      setActiveHeadingId(id);
    }
  };

  // タグクリック時の処理（Home画面と同じ処理）
  const handleTagClick = (e: React.MouseEvent<HTMLSpanElement>, tagSlug: string) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/${lang}${homePath === '/' ? '' : homePath}?tag=${encodeURIComponent(tagSlug)}`);
  };

  // 共有URLとテキストの生成
  const shareData = useMemo(() => {
    if (!post) return null;

    const postUrl = `${siteUrl}${lang === 'ja' ? '/ja' : '/en'}${postsPath}/${post.slug}`;
    const title = post.title;
    
    // タグの取得とフォーマット（#付き、スペース区切り）
    // post.tagsが存在し、配列で、かつ要素がある場合のみ処理
    const hasTags = post.tags && Array.isArray(post.tags) && post.tags.length > 0;
    const validTags = hasTags && post.tags
      ? post.tags.filter(tag => tag && typeof tag === 'string' && tag.trim())
      : [];
    
    // X用のタグフォーマット: ハイフンとスラッシュをアンダースコアに置き換え（Xはハイフンとスラッシュをハッシュタグとして認識しない）
    // その他のSNS用のタグフォーマット: ハイフンをそのまま保持
    const tagsForX = validTags.length > 0
      ? validTags.map(tag => {
          const cleaned = tag.trim().replace(/\s+/g, '');
          // ハイフンとスラッシュをアンダースコアに置き換え（Xのハッシュタグ仕様に合わせる）
          return `#${cleaned.replace(/[-/]/g, '_')}`;
        }).join(' ')
      : '';
    const tags = validTags.length > 0
      ? validTags.map(tag => `#${tag.trim().replace(/\s+/g, '')}`).join(' ')
      : '';
    
    // X用: タイトル + 改行 + タグ + 改行 + URL
    // タグがある場合は必ず含める（X用のフォーマットを使用：ハイフンとスラッシュをアンダースコアに置き換え）
    // タイトルとタグの間、タグとURLの間に改行を追加
    const shareTextForX = tagsForX ? `${title}\n${tagsForX}\n${postUrl}` : `${title}\n${postUrl}`;
    // Bluesky用: タイトル + 改行 + タグ + 改行 + URL（改行は\nで指定し、後でエンコード）
    const shareTextForBluesky = tags ? `${title}\n${tags}\n${postUrl}` : `${title}\n${postUrl}`;
    // Reddit用: タイトルのみ（Redditの慣習に合わせてタグは含めない）
    const shareTextForReddit = title;
    
    
    return {
      url: postUrl,
      title,
      tags,
      shareTextForX,
      shareTextForBluesky,
      shareTextForReddit,
    };
  }, [post, lang, postsPath, siteUrl]);

  // 共有機能のハンドラー
  const handleShareX = () => {
    if (!shareData) return;
    // Xはtextパラメータにタイトル、タグ、URLを含める（改行区切り）
    // intent/tweetを使用することで、小さいポップアップウィンドウが開き、
    // 背景のPost欄に情報が入力されることを防ぐ
    // urlパラメータは使用しない（textにURLが含まれているため重複を避ける）
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(shareData.shareTextForX)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareBluesky = () => {
    if (!shareData) return;
    // Blueskyは改行をサポートしているため、\nをエンコードして使用
    const url = `https://bsky.app/intent/compose?text=${encodeURIComponent(shareData.shareTextForBluesky)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareReddit = () => {
    if (!shareData) return;
    const url = `https://reddit.com/submit?url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.shareTextForReddit)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [copied, setCopied] = useState(false);
  const handleCopyUrl = async () => {
    if (!shareData) return;
    
    const copyText = shareData.tags
      ? `${shareData.title}\n${shareData.tags}\n${shareData.url}`
      : `${shareData.title}\n${shareData.url}`;
    
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // childrenからテキストを抽出する関数（React要素やオブジェクトを再帰的に処理）
  const extractTextFromChildren = (children: any): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (children && typeof children === 'object' && 'props' in children && children.props) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  // 見出しIDを取得する共通関数
  const getHeadingId = (level: number, text: string): string => {
    // headingsRef.currentを順番に走査し、現在のheadingRenderIndexRefの位置にある見出しのIDを返す
    // これにより、ReactMarkdownのレンダリング順序がTOC生成順序と一致することを前提とする
    const currentIndex = headingRenderIndexRef.current;
    
    if (currentIndex < headingsRef.current.length) {
      const heading = headingsRef.current[currentIndex];
      
      // 現在の見出しが、指定されたlevelとtextと一致するか確認
      // 一致しない場合は、headingsRef.currentを順番に走査して一致する見出しを探す
      if (heading.level === level && heading.text === text) {
        headingRenderIndexRef.current++;
        return heading.id;
      }
    }
    
    // 一致しない場合は、headingsRef.currentを順番に走査して一致する見出しを探す
    // これは、ReactMarkdownのレンダリング順序がTOC生成順序と一致しない場合のフォールバック
    // 同じテキストの見出しが複数ある場合、使用済みのインデックスを記録する
    const key = `${level}:${text}`;
    const usedIndices = headingUsageMapRef.current.get(key) || new Set<number>();
    
    for (let i = 0; i < headingsRef.current.length; i++) {
      const heading = headingsRef.current[i];
      if (heading.level === level && heading.text === text && !usedIndices.has(i)) {
        usedIndices.add(i);
        headingUsageMapRef.current.set(key, usedIndices);
        return heading.id;
      }
    }
    
    // 見つからない場合は、フォールバックIDを生成
    const fallbackId = `h${level}-fallback-${headingRenderIndexRef.current++}`;
    return fallbackId;
  };

  // ReactMarkdownのcomponentsをメモ化（早期リターンの前に定義する必要がある）
  // 各レンダリング前にheadingRenderIndexRefをリセットするため、useMemoの依存配列にtocを追加
  const markdownComponents = useMemo(() => {
    // コンポーネント作成時にリセット
    headingRenderIndexRef.current = 0;
    headingUsageMapRef.current.clear(); // 使用済みインデックスマップをクリア
    
    return {
    h1({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(1, text);
      return (
        <h1 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h1>
      );
    },
    h2({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(2, text);
      return (
        <h2 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h2>
      );
    },
    h3({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(3, text);
      
      return (
        <h3 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h3>
      );
    },
    h4({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(4, text);
      return (
        <h4 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h4>
      );
    },
    h5({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(5, text);
      return (
        <h5 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h5>
      );
    },
    h6({ children, ...props }: any) {
      const text = extractTextFromChildren(children).trim();
      const id = getHeadingId(6, text);
      return (
        <h6 id={id} className="markdown-heading" {...props}>
          <a
            href={`#${id}`}
            className="markdown-heading__anchor"
            aria-label={i18n.postDetail.headingLink(String(children).trim())}
            onClick={(e) => handleHeadingClick(e, id)}
          >
            #
          </a>
          {children}
        </h6>
      );
    },
    code({ className, children, ...props }: any) {
      // language-xxx または language-xxx:filename の形式を解析（ファイル名は無視）
      const match = /language-([^:]+)/.exec(className || '');
      const language = match ? match[1] : '';
      const codeString = String(children).replace(/\n$/, '');
      const isInline = !className || !className.includes('language-');

      // インラインコードの場合は通常のcodeタグを返す
      if (isInline) {
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      }

      // Mermaidの場合はMermaidコンポーネントを使用
      if (language === 'mermaid') {
        return <Mermaid chart={codeString} />;
      }

      // その他のコードブロックはシンタックスハイライトを適用
      return (
        <SyntaxHighlighter
          language={language || 'text'}
          style={vscDarkPlus}
          PreTag="div"
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      );
    },
    table({ children, ...props }: any) {
      return (
        <table {...props}>
          {children}
        </table>
      );
    },
    thead({ children, ...props }: any) {
      return (
        <thead {...props}>
          {children}
        </thead>
      );
    },
    tbody({ children, ...props }: any) {
      return (
        <tbody {...props}>
          {children}
        </tbody>
      );
    },
    tr({ children, ...props }: any) {
      return (
        <tr {...props}>
          {children}
        </tr>
      );
    },
    th({ children, ...props }: any) {
      return (
        <th {...props}>
          {children}
        </th>
      );
    },
    td({ children, ...props }: any) {
      return (
        <td {...props}>
          {children}
        </td>
      );
    },
    p({ children, ...props }: any) {
      // 子要素に`post:`形式のリンクが含まれているかチェック
      // ArticleLinkCardが含まれる場合は`<div>`を返す（`<p>`の中に`<div>`を入れることはできないため）
      const hasPostIdLink = (() => {
        if (props.node?.children) {
          return props.node.children.some((child: any) => {
            if (child.type === 'element' && child.tagName === 'a') {
              const href = child.properties?.href || '';
              // `post:`で始まる場合はtrue
              if (typeof href === 'string' && href.startsWith('post:')) {
                return true;
              }
              // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
              if (href === '' && child.position && post?.content) {
                const lines = post.content.split('\n');
                const lineIndex = child.position.start.line - 1;
                if (lineIndex >= 0 && lineIndex < lines.length) {
                  const line = lines[lineIndex];
                  const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
                  if (match) {
                    return true;
                  }
                }
              }
            }
            return false;
          });
        }
        return false;
      })();
      
      if (hasPostIdLink) {
        // `post:`形式のリンクが含まれている場合は`<div>`を返す
        return <div {...props}>{children}</div>;
      }
      
      // 通常の`<p>`タグを返す
      return <p {...props}>{children}</p>;
    },
    a({ href, children, ...props }: any) {
      // hrefが空文字列の場合、props.node.properties.hrefを確認
      // ReactMarkdownが`post:`を無効なURLとして扱い、空文字列に変換する可能性がある
      let actualHref = href;
      if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.properties?.href) {
        actualHref = props.node.properties.href;
      }
      
      // hrefが空文字列の場合、元のMarkdownテキストから抽出を試みる
      if ((!actualHref || (typeof actualHref === 'string' && actualHref.trim() === '')) && props.node?.position && post?.content) {
        const lines = post.content.split('\n');
        const lineIndex = props.node.position.start.line - 1;
        if (lineIndex >= 0 && lineIndex < lines.length) {
          const line = lines[lineIndex];
          // `[](post:post_id)`形式を抽出
          const match = line.match(/\[([^\]]*)\]\(post:([^\)]+)\)/);
          if (match) {
            actualHref = `post:${match[2]}`;
          }
        }
      }
      
      // hrefがない場合は通常のリンクを返す
      if (!actualHref || typeof actualHref !== 'string') {
        return (
          <a href={actualHref} {...props}>
            {children}
          </a>
        );
      }

      // PostID形式のリンクをチェック（`post:post_id`形式）
      const isPostIdLink = typeof actualHref === 'string' && actualHref.startsWith('post:');
      if (isPostIdLink) {
        const postId = actualHref.substring(5); // 'post:'を除く
        // リンクテキストが空（`[]`）の場合のみPostIDベースのカードを表示
        const linkText = extractTextFromChildren(children);
        if (!linkText || linkText.trim() === '') {
          return <ArticleLinkCardWrapperByPostId postId={postId} lang={lang} />;
        }
      }
      
      // actualHrefをhrefとして使用
      href = actualHref;

      // 自ドメインURLかどうかを判定
      if (isOwnDomainUrl(href)) {
        // slugを抽出
        const slug = extractSlugFromUrl(href, lang);
        // URLから言語コードを抽出（URL内の言語コードを優先）
        const urlLang = extractLangFromUrl(href) || lang;
        if (slug) {
          // 自ドメインの記事URLの場合はArticleLinkCardを表示
          // URL内の言語コードを使用して記事を取得
          return <ArticleLinkCardWrapper href={href} slug={slug} lang={urlLang} />;
        }
      }

      // 外部URLまたはslug抽出に失敗した場合は通常のリンクを返す
      const isOwnDomain = isOwnDomainUrl(href);
      return (
        <a 
          href={href} 
          {...props} 
          {...(isOwnDomain ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        >
          {children}
        </a>
      );
    },
    iframe({ src, width, height, ...props }: any) {
      // YouTube埋め込みの場合、レスポンシブラッパーで囲む
      if (src && (src.includes('youtube.com/embed') || src.includes('youtu.be'))) {
        return (
          <div className="embed-responsive">
            <iframe
              src={src}
              width={width || '560'}
              height={height || '315'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              {...props}
            />
          </div>
        );
      }
      // その他のiframeは通常通り
      return (
        <iframe
          src={src}
          width={width}
          height={height}
          {...props}
        />
      );
    },
    };
  }, [toc, lang]);

  if (loading) {
    return (
      <div className="post-detail">
        {/* 言語案内バナー */}
        <LanguageNoticeBanner />
        <div className="post-detail__container">
          <LoadingSpinner text={i18n.common.loading} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-detail">
        {/* 言語案内バナー */}
        <LanguageNoticeBanner />
        <div className="post-detail__container">
          <div className="post-detail__not-found">
            <h1>{i18n.postDetail.errorOccurred}</h1>
            <p>{error}</p>
            {slug && (
              <p style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
                {i18n.postDetail.slugLabel}{slug}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail">
        {/* 言語案内バナー */}
        <LanguageNoticeBanner />
        <div className="post-detail__container">
          <div className="post-detail__not-found">
            <h1>{i18n.postDetail.postNotFound}</h1>
            <p>{i18n.postDetail.postNotFoundDescription}</p>
          </div>
        </div>
      </div>
    );
  }

  // 公開済み記事のみ表示
  if (post.status !== 'published') {
    return (
      <div className="post-detail">
        {/* 言語案内バナー */}
        <LanguageNoticeBanner />
        <div className="post-detail__container">
          <div className="post-detail__not-found">
            <h1>{i18n.postDetail.postNotPublished}</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-detail">
      {/* 言語案内バナー */}
      <LanguageNoticeBanner />
      <div className="post-detail__container">
        <div className="post-detail__layout">
          <div className="post-detail__main">
          <article className="post-detail__article">
          {displayThumbnailUrl && (
            <div className="post-detail__thumbnail">
              <img
                src={displayThumbnailUrl}
                alt={post.title}
                className="post-detail__thumbnail-image"
                onError={handleImageError}
              />
            </div>
          )}
          
          <header className="post-detail__header">
            {getCategoryName && (
              <div className="post-detail__category">{getCategoryName}</div>
            )}
            <h1 className="post-detail__title">{post.title}</h1>
            <div className="post-detail__meta">
              {post.published_at && (
                <span className="post-detail__date">
                  {i18n.postDetail.publishedDate}{formatDate(post.published_at)}
                </span>
              )}
              {post.published_at && post.updated_at !== post.published_at && (
                <span className="post-detail__updated">
                  {i18n.postDetail.updatedDate}{formatDate(post.updated_at)}
                </span>
              )}
              {!post.published_at && (
                <span className="post-detail__updated">
                  {i18n.postDetail.updatedDate}{formatDate(post.updated_at)}
                </span>
              )}
            </div>
          </header>

          {post.tags && post.tags.length > 0 && (
            <div className="post-detail__tags">
              {post.tags.map((tag, index) => {
                // tag_slugsが存在する場合はslugを使用（優先）
                // タグ名はAPIから既に適切な言語で返されているため、そのまま使用
                let tagSlug: string;
                if (post.tag_slugs && post.tag_slugs[index] && post.tag_slugs[index].trim()) {
                  tagSlug = post.tag_slugs[index].trim();
                } else {
                  // tag_slugsが存在しない場合、タグ名からslugを生成（フォールバック）
                  tagSlug = tag.toLowerCase().replace(/\s+/g, '-');
                }
                
                return (
                  <span
                    key={`${tagSlug}-${index}`}
                    className="post-detail__tag post-detail__tag--clickable"
                    onClick={(e) => handleTagClick(e, tagSlug)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTagClick(e as any, tagSlug);
                      }
                    }}
                    aria-label={i18n.postDetail.filterByTag(tag)}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>
          )}

          <div className="post-detail__content">
            {post.excerpt && (
              <p className="post-detail__excerpt">{post.excerpt}</p>
            )}
            <div 
              className="post-detail__body markdown-content"
              style={{
                '--scroll-margin-top': `${headerHeight + 16}px`,
              } as React.CSSProperties}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* 共有アイコン */}
            <div className="post-detail__share">
              <button
                type="button"
                onClick={handleShareX}
                className="post-detail__share-button"
                aria-label={i18n.postDetail.shareOnX}
                title={i18n.postDetail.shareOnX}
              >
                <img src="/icons/x.svg" alt="X" className="post-detail__share-icon" />
              </button>
              <button
                type="button"
                onClick={handleShareBluesky}
                className="post-detail__share-button"
                aria-label={i18n.postDetail.shareOnBluesky}
                title={i18n.postDetail.shareOnBluesky}
              >
                <img src="/icons/bluesky.svg" alt="Bluesky" className="post-detail__share-icon" />
              </button>
              <button
                type="button"
                onClick={handleShareReddit}
                className="post-detail__share-button"
                aria-label={i18n.postDetail.shareOnReddit}
                title={i18n.postDetail.shareOnReddit}
              >
                <img src="/icons/reddit.svg" alt="Reddit" className="post-detail__share-icon" />
              </button>
              <button
                type="button"
                onClick={handleCopyUrl}
                className={`post-detail__share-button ${copied ? 'post-detail__share-button--copied' : ''}`}
                aria-label={i18n.postDetail.copyUrl}
                title={copied ? i18n.postDetail.urlCopied : i18n.postDetail.copyUrl}
              >
                {copied ? (
                  <img src="/icons/check.svg" alt="Copied" className="post-detail__share-icon post-detail__share-icon--check" />
                ) : (
                  <img src="/icons/copy.svg" alt="Copy" className="post-detail__share-icon" />
                )}
              </button>
              {/* Buy me a coffee ボタン */}
              <a
                href="https://www.buymeacoffee.com/version0.1"
                target="_blank"
                rel="noopener noreferrer"
                className="post-detail__share-button"
                aria-label={i18n.postDetail.buyMeACoffee}
                title={i18n.postDetail.buyMeACoffee}
              >
                <img src="/icons/bmc.svg" alt="Buy Me A Coffee" className="post-detail__share-icon" />
              </a>
            </div>
          </div>
        </article>

        {relatedPosts.length > 0 && (
          <section className="post-detail__related" aria-labelledby="post-detail-related-heading">
            <h2 id="post-detail-related-heading" className="post-detail__related-title">
              {i18n.postDetail.relatedArticles}
            </h2>
            <ul className="post-detail__related-list">
              {relatedPosts.map((relatedPost) => (
                <li key={relatedPost.post_id}>
                  <ArticleLinkCard
                    post={relatedPost}
                    lang={lang}
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}
          </div>

        {toc.length > 0 && (
          <aside 
            className="post-detail__toc"
            style={{
              top: `${headerHeight}px`,
              maxHeight: `calc(100vh - ${headerHeight}px)`,
            }}
          >
            <div 
              className="post-detail__toc-sticky"
              style={{
                maxHeight: `calc(100vh - ${headerHeight}px)`,
              }}
            >
              <div className="post-detail__toc-header">
                <h2 className="post-detail__toc-title">{i18n.postDetail.tableOfContents}</h2>
                <button
                  className="post-detail__toc-toggle"
                  onClick={() => setIsTocExpanded(!isTocExpanded)}
                  aria-label={isTocExpanded ? '目次を閉じる' : '目次を開く'}
                  aria-expanded={isTocExpanded}
                >
                  <span className={`post-detail__toc-toggle-icon ${isTocExpanded ? 'post-detail__toc-toggle-icon--expanded' : ''}`}>
                    ▼
                  </span>
                </button>
              </div>
              <nav className={`post-detail__toc-nav ${isTocExpanded ? 'post-detail__toc-nav--expanded' : ''}`}>
                <ul className="post-detail__toc-list">
                  {toc.map((heading) => (
                    <li
                      key={heading.id}
                      className={`post-detail__toc-item post-detail__toc-item--level-${heading.level} ${
                        activeHeadingId === heading.id ? 'post-detail__toc-item--active' : ''
                      }`}
                    >
                      <a
                        href={`#${heading.id}`}
                        onClick={(e) => handleHeadingClick(e, heading.id)}
                        className="post-detail__toc-link"
                      >
                        {heading.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>
        )}
        </div>
      </div>
    </div>
  );
}

export default PostDetail;

